import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useAppTheme } from '../features/theme/themeContext'
import { useAppSelector } from '../store'
import { selectShortName } from '../store/slices/userSlice'
import { selectAccount } from '../store/slices/accountSlice'
import { YmRouteTracker } from '../components/YmRouteTracker'

const navigation = [
  { name: 'Главная', href: '/dashboard', icon: 'heroicons:home' },
  { name: 'Счета', href: '/invoices', icon: 'heroicons:document-text' },
  { name: 'Профиль', href: '/profile', icon: 'heroicons:user' },
  { name: 'Поддержка', href: '/support', icon: 'heroicons:chat-bubble-left-right' },
]

const MOBILE_BOTTOM_NAV_INNER_HEIGHT = '4rem'
const MOBILE_BOTTOM_NAV_TOTAL_HEIGHT = `calc(${MOBILE_BOTTOM_NAV_INNER_HEIGHT} + env(safe-area-inset-bottom, 0px))`
const BITRIX_LOCK_CLASS_RE = /(bitrix|b24|bx24|bx-livechat|livechat)/i

function hasBitrixResidue() {
  return !!document.querySelector(
    'script[data-bitrix-widget="1"], [id^="bx-livechat"], [class*="b24-widget"], [class*="bx24-widget"], .bx-livechat-wrapper',
  )
}

function clearBitrixLockClasses(el: HTMLElement) {
  const toRemove = Array.from(el.classList).filter((name) => BITRIX_LOCK_CLASS_RE.test(name))
  for (const name of toRemove) {
    el.classList.remove(name)
  }
}

function resetBitrixGlobalLayoutSideEffects() {
  if (document.body.classList.contains('bitrix-chat-inline')) return
  if (!hasBitrixResidue()) return

  const body = document.body
  const html = document.documentElement

  if (body.style.position === 'fixed' || body.style.position === 'absolute') {
    body.style.position = ''
  }
  if (body.style.top !== '') body.style.top = ''
  if (body.style.left !== '') body.style.left = ''
  if (body.style.right !== '') body.style.right = ''
  if (body.style.bottom !== '') body.style.bottom = ''
  if (['100vh', '100%', '100dvh', '100svh'].includes(body.style.height)) {
    body.style.height = ''
  }
  if (['100vw', '100%'].includes(body.style.width)) {
    body.style.width = ''
  }
  if (body.style.overflow === 'hidden') {
    body.style.overflow = ''
  }
  if (body.style.touchAction === 'none') {
    body.style.touchAction = ''
  }
  if (body.style.overscrollBehavior !== '') {
    body.style.overscrollBehavior = ''
  }
  if (body.style.overscrollBehaviorY !== '') {
    body.style.overscrollBehaviorY = ''
  }
  if (['100vh', '100%', '100dvh', '100svh'].includes(html.style.height)) {
    html.style.height = ''
  }

  clearBitrixLockClasses(body)
  clearBitrixLockClasses(html)
}

export function DefaultLayout() {
  const location = useLocation()
  const shortName = useAppSelector(selectShortName)
  const account = useAppSelector(selectAccount)
  const { isDark, toggle } = useAppTheme()
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isSupportRoute = location.pathname === '/support' || location.pathname.startsWith('/support/')

  useEffect(() => {
    if (isSupportRoute) return

    resetBitrixGlobalLayoutSideEffects()

    const observer = new MutationObserver(() => {
      resetBitrixGlobalLayoutSideEffects()
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: false,
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    return () => observer.disconnect()
  }, [isSupportRoute])

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/')

  const themeIcon = isDark ? 'heroicons:sun-solid' : 'heroicons:moon-solid'

  return (
    <div
      data-app-layout-shell="true"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <YmRouteTracker />

      {/* Header */}
      <header
        id="app-layout-header"
        data-app-layout-header="true"
        className={`fixed top-0 left-0 right-0 z-99999 transition-all duration-300 ${
          isScrolled ? 'header-blur shadow-lg' : 'header-transparent'
        }`}
        style={{
          // Не полагаемся только на utility-классы: сторонний CSS (Bitrix)
          // может переопределить .fixed/.top-0/.left-0/.right-0.
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 99999,
          // iOS standalone PWA: status bar overlap. С viewport-fit=cover и
          // status-bar-style=black-translucent контент идёт под status bar.
          paddingTop: 'env(safe-area-inset-top, 0)',
          paddingLeft: 'env(safe-area-inset-left, 0)',
          paddingRight: 'env(safe-area-inset-right, 0)',
        }}
      >
        <div className="container mx-auto max-w-5xl px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="ПЖ19" className="h-8" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  data-ym-click={`nav_header_${item.href.replace('/', '') || 'dashboard'}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-[#F7941D] bg-[#F7941D]/10'
                      : 'text-(--text-secondary) hover:text-(--text-primary) hover:bg-(--glass-bg)'
                  }`}
                >
                  <Icon icon={item.icon} className="w-5 h-5" />
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop profile */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={toggle}
                data-ym-click="theme_toggle_desktop"
                className="theme-toggle"
                title={isDark ? 'Светлая тема' : 'Тёмная тема'}
              >
                <Icon icon={themeIcon} className="w-5 h-5" />
              </button>
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{shortName}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Договор {account?.contractNumber ?? '—'}
                </p>
              </div>
            </div>

            {/* Mobile header icons */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={toggle}
                data-ym-click="theme_toggle_mobile"
                className="p-2 rounded-lg transition-colors hover:text-[#F7941D]"
                style={{ color: 'var(--text-muted)' }}
              >
                <Icon icon={themeIcon} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content. pt-16 = высота header (h-16), плюс safe-area-inset-top
          для iOS standalone PWA где контент идёт под status bar. */}
      <main
        className={`flex-1 ${isSupportRoute ? 'pb-0 md:pb-12' : 'pb-20 md:pb-12'}`}
        style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0))' }}
      >
        <div className="container mx-auto max-w-5xl px-4 pt-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        id="app-mobile-bottom-nav"
        data-app-mobile-bottom-nav="true"
        aria-label="Нижняя навигация"
        className="md:hidden fixed bottom-0 left-0 right-0 z-99999 backdrop-blur-lg border-t"
        style={{
          // Критично: бар должен быть полностью независим от CSS Bitrix/виджетов.
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          boxSizing: 'border-box',
          background: 'var(--header-blur-bg)',
          borderColor: 'var(--glass-border)',
          // ВАЖНО: общая высота бара всегда фиксирована.
          height: MOBILE_BOTTOM_NAV_TOTAL_HEIGHT,
          minHeight: MOBILE_BOTTOM_NAV_TOTAL_HEIGHT,
          maxHeight: MOBILE_BOTTOM_NAV_TOTAL_HEIGHT,
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          paddingLeft: 'env(safe-area-inset-left, 0)',
          paddingRight: 'env(safe-area-inset-right, 0)',
        }}
      >
        <div
          data-app-bottom-nav-inner="true"
          className="flex items-center justify-between h-16 px-2 gap-2"
          style={{
            height: MOBILE_BOTTOM_NAV_INNER_HEIGHT,
            minHeight: MOBILE_BOTTOM_NAV_INNER_HEIGHT,
            maxHeight: MOBILE_BOTTOM_NAV_INNER_HEIGHT,
          }}
        >
          {navigation.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              data-ym-click={`nav_mobile_${item.href.replace('/', '') || 'dashboard'}`}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 min-w-0 rounded-lg transition-colors ${
                isActive(item.href) ? 'text-[#F7941D]' : 'text-(--text-muted)'
              }`}
            >
              <Icon icon={item.icon} className="w-6 h-6 shrink-0" />
              <span className="text-xs font-medium truncate">{item.name}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  )
}
